// =====================================================================
//  Cotización desde el navegador — SrSimi (Dr Envío) y ELogicYa (EnviaYa)
//  Ambas APIs permiten CORS, así que cotizan sin backend ni créditos Base44.
//  Autor: JORGE ALBERTO BAEZ PEREZ
// =====================================================================

import { CARRIERS } from "@/lib/carriers";

const num = (v) => { const n = parseFloat(String(v).replace(",", ".")); return isFinite(n) ? n : 0; };
const etaFromDays = (d) => { const x = new Date(); x.setDate(x.getDate() + (Number(d) || 1)); return x.toISOString().slice(0, 10); };
const parseDays = (v) => { const m = String(v || "").match(/\d+/); return m ? parseInt(m[0], 10) || 1 : 1; };

function withMargin(rate, pct) {
  const cost = Number(rate.total_cost) || 0;
  const margin = cost * ((Number(pct) || 0) / 100);
  return { ...rate, provider_cost: Number(cost.toFixed(2)), platform_margin: Number(margin.toFixed(2)), total_cost: Number((cost + margin).toFixed(2)), eta: etaFromDays(rate.transit_days) };
}

const pkgType = (pt) => (pt === "sobre" ? "envelope" : pt === "tarima" || pt === "camion" ? "pallet" : "box");

export async function quoteSrSimiClient(cfg, p) {
  if (!cfg.srsimi_token) return [];
  const base = cfg.srsimi_mode === "sandbox" ? "https://sandbox.api-drenvio.com/v2" : "https://prod.api-drenvio.com/v2";
  const carriers = CARRIERS.filter(c => (c.aggregators || []).includes("srsimi") && c.drenvioCode);
  const codeToId = Object.fromEntries(carriers.map(c => [c.drenvioCode, c.id]));
  const w = Math.max(num(p.weight_kg) || 1, 0.1);
  const L = Math.max(num(p.length_cm) || 1, 1), W = Math.max(num(p.width_cm) || 1, 1), H = Math.max(num(p.height_cm) || 1, 1);
  const body = {
    type: "National",
    origin: { country: "MX", postal_code: String(p.origin_cp || "") },
    destination: { country: "MX", postal_code: String(p.dest_cp || "") },
    packages: [{ weight: w, height: H, width: W, length: L, volumetric: Number(((L * W * H) / 5000).toFixed(2)), type: pkgType(p.package_type), main_weight: w }],
    carriers: carriers.map(c => c.drenvioCode),
    insurance: 0
  };
  const res = await fetch(`${base}/shipments/rate`, {
    method: "POST",
    headers: { "Authorization": `Bearer ${cfg.srsimi_token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
  if (!res.ok) return [];
  const data = await res.json().catch(() => []);
  const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
  return list.map(item => {
    const carrier = codeToId[item.carrier];
    const total = num(item.price);
    if (!carrier || !total) return null;
    return withMargin({
      carrier, service: item.service || "Nacional", base_cost: total, total_cost: total,
      transit_days: parseDays(item.days), source: "srsimi", aggregator: "srsimi",
      drenvioServiceId: item.service_id || "", drenvioObjectId: item.ObjectId == null ? "" : String(item.ObjectId),
      drenvioShippingId: item.ShippingId || "", drenvioCarrier: item.carrier || "", drenvioPrice: total
    }, cfg.srsimi_markup_value);
  }).filter(Boolean);
}

export async function quoteElogicYaClient(cfg, p) {
  if (!cfg.elogicya_api_key) return [];
  const base = cfg.elogicya_mode === "production" ? "https://enviaya.com.mx/api/v1" : "https://staging.enviaya.com.mx/api/v1";
  const norm = (c) => String(c || "").toLowerCase().replace(/[\s_-]/g, "");
  const codeToId = Object.fromEntries(CARRIERS.filter(c => c.elogicyaCode).map(c => [norm(c.elogicyaCode), c.id]));
  const w = Math.max(num(p.weight_kg) || 1, 0.1);
  const body = {
    api_key: cfg.elogicya_api_key,
    shipment: {
      shipment_type: p.package_type === "sobre" ? "Document" : "Package",
      parcels: [{ quantity: "1", weight: w.toFixed(2), weight_unit: "kg", length: String(Math.max(num(p.length_cm) || 1, 1)), height: String(Math.max(num(p.height_cm) || 1, 1)), width: String(Math.max(num(p.width_cm) || 1, 1)), dimension_unit: "cm", content: "Mercancía" }]
    },
    origin_direction: { country_code: "MX", postal_code: String(p.origin_cp || "") },
    destination_direction: { country_code: "MX", postal_code: String(p.dest_cp || "") },
    insured_value: "5000", insured_value_currency: "MXN", intelligent_filtering: true, enable_cached_rates: true, timeout: 20
  };
  const post = (withAccount) => fetch(`${base}/rates`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${cfg.elogicya_api_key}` },
    body: JSON.stringify(withAccount && cfg.elogicya_account ? { ...body, enviaya_account: cfg.elogicya_account } : body)
  });
  let res = await post(true);
  if (!res.ok && cfg.elogicya_account) res = await post(false);
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  const skip = new Set(["warning", "error", "errors", "meta", "message", "status"]);
  const list = Array.isArray(data?.rates) ? data.rates : Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data
    : Object.entries(data || {}).filter(([k, v]) => !skip.has(k) && Array.isArray(v)).flatMap(([, v]) => v);
  const out = [];
  for (const r of list) {
    if (!r || typeof r !== "object") continue;
    const envCarrier = r.carrier || r.carrier_code || "";
    const carrier = codeToId[norm(envCarrier)];
    const total = Number(r.total_amount || r.public_total_amount || r.net_total_amount || r.price || 0);
    if (!carrier || !total) continue;
    const hours = Number(r.est_transit_time_hours || 0);
    const days = hours ? Math.max(1, Math.round(hours / 24)) : (Number(r.delivery_days || r.estimated_delivery_days || r.transit_days || 0) || 1);
    out.push(withMargin({
      carrier, service: r.dynamic_service_name || r.carrier_service_name || r.enviaya_service_name || r.service || "Nacional",
      base_cost: total, total_cost: total, transit_days: days, source: "elogicya", aggregator: "elogicya",
      elogicyaRateId: r.rate_id != null ? String(r.rate_id) : (r.id != null ? String(r.id) : ""),
      elogicyaServiceCode: r.carrier_service_code || r.enviaya_service_code || r.service_code || "",
      elogicyaCarrier: envCarrier
    }, cfg.elogicya_markup_value));
  }
  return out;
}